import { useEffect, useRef, useState } from "react";
import { openRearCamera, captureFrame } from "../lib/camera";

const MIN_MARKERS = 12;

type Section = "hero" | "mission" | "markers";

// Hero and mission use the device's native camera app (via <input capture>) so
// photographers can rotate to landscape and get a true landscape-orientation
// JPEG with EXIF. The in-app viewfinder is locked to page orientation.
const NATIVE_CAMERA_SECTIONS: Section[] = ["hero", "mission"];

export interface PhotoSet {
  hero: Blob;
  mission: Blob;
  markers: Blob[];
}

interface PhotoCaptureProps {
  onCapture: (photos: PhotoSet) => void;
  initialPhotos?: { hero: Blob | null; mission: Blob | null; markers: Blob[] };
}

export function PhotoCapture({ onCapture, initialPhotos }: PhotoCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [activeSection, setActiveSection] = useState<Section>("hero");
  const [hero, setHero] = useState<Blob | null>(initialPhotos?.hero ?? null);
  const [heroThumb, setHeroThumb] = useState<string | null>(null);
  const [mission, setMission] = useState<Blob | null>(initialPhotos?.mission ?? null);
  const [missionThumb, setMissionThumb] = useState<string | null>(null);
  const [markers, setMarkers] = useState<Blob[]>(initialPhotos?.markers ?? []);
  const [markerThumbs, setMarkerThumbs] = useState<string[]>([]);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const heroThumbRef = useRef<string | null>(null);
  const missionThumbRef = useRef<string | null>(null);
  const markerThumbsRef = useRef<string[]>([]);
  const heroInputRef = useRef<HTMLInputElement>(null);
  const heroGalleryInputRef = useRef<HTMLInputElement>(null);
  const missionInputRef = useRef<HTMLInputElement>(null);
  const missionGalleryInputRef = useRef<HTMLInputElement>(null);
  const markerImportRef = useRef<HTMLInputElement>(null);

  const useNativeCamera = NATIVE_CAMERA_SECTIONS.includes(activeSection);

  // Re-create thumbnails for initial photos
  useEffect(() => {
    if (initialPhotos?.hero) {
      const url = URL.createObjectURL(initialPhotos.hero);
      heroThumbRef.current = url;
      setHeroThumb(url);
    }
    if (initialPhotos?.mission) {
      const url = URL.createObjectURL(initialPhotos.mission);
      missionThumbRef.current = url;
      setMissionThumb(url);
    }
    if (initialPhotos?.markers.length) {
      const urls = initialPhotos.markers.map((b) => URL.createObjectURL(b));
      markerThumbsRef.current = urls;
      setMarkerThumbs(urls);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Only hold the in-app camera open for the markers section. Hero/mission
    // hand off to the native camera app, which needs exclusive access on iOS.
    if (useNativeCamera) {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      if (videoRef.current) videoRef.current.srcObject = null;
      setCameraError(null);
      return;
    }

    let active = true;
    openRearCamera()
      .then((stream) => {
        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      })
      .catch((err: unknown) => {
        if (active) {
          setCameraError(
            err instanceof Error ? err.message : "Camera unavailable",
          );
        }
      });

    return () => {
      active = false;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [useNativeCamera]);

  // Clean up object URLs on unmount — uses refs to access latest values
  useEffect(() => {
    return () => {
      if (heroThumbRef.current) URL.revokeObjectURL(heroThumbRef.current);
      if (missionThumbRef.current) URL.revokeObjectURL(missionThumbRef.current);
      markerThumbsRef.current.forEach((u) => URL.revokeObjectURL(u));
    };
  }, []);

  async function takePhoto() {
    if (!videoRef.current) return;
    try {
      const blob = await captureFrame(videoRef.current);
      const url = URL.createObjectURL(blob);
      markerThumbsRef.current = [...markerThumbsRef.current, url];
      setMarkers((prev) => [...prev, blob]);
      setMarkerThumbs((prev) => [...prev, url]);
    } catch (err) {
      console.error("captureFrame failed", err);
    }
  }

  // Downstream zip writes these blobs as `.jpg` and recognition tooling assumes
  // JPEG bytes — reject non-JPEG selections at the boundary rather than
  // silently writing PNG/HEIC payloads with a `.jpg` filename.
  function isJpeg(file: File): boolean {
    return file.type === "image/jpeg" || /\.jpe?g$/i.test(file.name);
  }

  async function onNativePhotoSelected(
    section: "hero" | "mission",
    e: React.ChangeEvent<HTMLInputElement>,
  ) {
    const file = e.target.files?.[0];
    // Reset so picking the same file twice still fires onChange.
    e.target.value = "";
    if (!file) return;
    if (!isJpeg(file)) {
      setSelectionError(
        `${section === "hero" ? "Hero" : "Mission"} photo must be a JPEG (got ${file.type || "unknown type"}). Re-shoot with the camera app or convert before importing.`,
      );
      return;
    }
    setSelectionError(null);
    // Eagerly read into memory — File holds a lazy disk reference that goes
    // stale on mobile after navigation; Blob does not.
    const blob = new Blob([await file.arrayBuffer()], { type: file.type });
    const url = URL.createObjectURL(blob);
    if (section === "hero") {
      if (heroThumbRef.current) URL.revokeObjectURL(heroThumbRef.current);
      heroThumbRef.current = url;
      setHero(blob);
      setHeroThumb(url);
    } else {
      if (missionThumbRef.current) URL.revokeObjectURL(missionThumbRef.current);
      missionThumbRef.current = url;
      setMission(blob);
      setMissionThumb(url);
    }
  }

  async function onMarkersImported(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    // Reset so picking the same file(s) twice still fires onChange.
    e.target.value = "";
    if (!files.length) return;
    const accepted = files.filter(isJpeg);
    const rejectedCount = files.length - accepted.length;
    setSelectionError(
      rejectedCount > 0
        ? `Skipped ${rejectedCount} non-JPEG file${rejectedCount === 1 ? "" : "s"}. Markers must be JPEG.`
        : null,
    );
    if (!accepted.length) return;
    // Eagerly read into memory — File holds a lazy disk reference that goes
    // stale on mobile after navigation; Blob does not.
    const blobs = await Promise.all(
      accepted.map(async (f) => new Blob([await f.arrayBuffer()], { type: f.type })),
    );
    const urls = blobs.map((b) => URL.createObjectURL(b));
    markerThumbsRef.current = [...markerThumbsRef.current, ...urls];
    setMarkers((prev) => [...prev, ...blobs]);
    setMarkerThumbs((prev) => [...prev, ...urls]);
  }

  function retakeMarker(index: number) {
    if (!videoRef.current) return;
    captureFrame(videoRef.current)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        setMarkers((prev) => {
          const next = [...prev];
          next[index] = blob;
          return next;
        });
        setMarkerThumbs((prev) => {
          const old = prev[index];
          if (old) URL.revokeObjectURL(old);
          const next = [...prev];
          next[index] = url;
          markerThumbsRef.current = next;
          return next;
        });
      })
      .catch((err) => console.error("retakeMarker failed", err));
  }

  const canProceed = hero !== null && mission !== null && markers.length >= MIN_MARKERS;

  const sectionLabel: Record<Section, string> = {
    hero: "Hero Photo (displayed on hunt card)",
    mission: "Mission Photo (shown during POI mission)",
    markers: `Marker Photos (${markers.length} / ${MIN_MARKERS})`,
  };

  const captureLabel: Record<Section, string> = {
    hero: hero ? "Retake Hero (open camera)" : "Open camera — take Hero photo",
    mission: mission ? "Retake Mission (open camera)" : "Open camera — take Mission photo",
    markers: "Take Marker Photo",
  };

  function triggerNativeCamera() {
    if (activeSection === "hero") heroInputRef.current?.click();
    else if (activeSection === "mission") missionInputRef.current?.click();
  }

  function triggerGalleryPicker() {
    if (activeSection === "hero") heroGalleryInputRef.current?.click();
    else if (activeSection === "mission") missionGalleryInputRef.current?.click();
  }

  return (
    <div style={s.container}>
      <h2 style={s.heading}>Step 2 — Photos</h2>

      {/* Section tabs */}
      <div style={s.tabRow}>
        {(["hero", "mission", "markers"] as Section[]).map((sec) => {
          const isActive = activeSection === sec;
          const isDone =
            sec === "hero" ? hero !== null :
            sec === "mission" ? mission !== null :
            markers.length >= MIN_MARKERS;
          return (
            <button
              key={sec}
              style={{
                ...s.tab,
                ...(isActive ? s.tabActive : {}),
                ...(isDone && !isActive ? s.tabDone : {}),
              }}
              onClick={() => setActiveSection(sec)}
              aria-pressed={isActive}
            >
              {sec === "hero" ? "Hero" : sec === "mission" ? "Mission" : "Markers"}
              {isDone && " ✓"}
            </button>
          );
        })}
      </div>

      <p style={s.sectionLabel}>{sectionLabel[activeSection]}</p>

      {selectionError && (
        <p style={s.error} role="alert">
          {selectionError}
        </p>
      )}

      {useNativeCamera ? (
        <p style={s.nativeHint}>
          Opens your phone&apos;s camera app. Rotate to landscape for wide shots —
          orientation is preserved. Or tap <strong>From file</strong> to upload an existing JPEG.
        </p>
      ) : cameraError ? (
        <p style={s.error}>{cameraError}</p>
      ) : (
        <div style={s.viewfinderWrap}>
          <div style={s.overlay}>
            <span style={s.overlayText}>
              Vary angle and distance — straight-on, left, right, close-up
            </span>
          </div>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={s.video}
            aria-label="Camera viewfinder"
          />
        </div>
      )}

      {/* Camera inputs for hero/mission — `capture` forces native camera app */}
      <input
        ref={heroInputRef}
        type="file"
        accept="image/jpeg"
        capture="environment"
        style={s.hiddenInput}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => onNativePhotoSelected("hero", e)}
      />
      <input
        ref={missionInputRef}
        type="file"
        accept="image/jpeg"
        capture="environment"
        style={s.hiddenInput}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => onNativePhotoSelected("mission", e)}
      />
      {/* Gallery inputs for hero/mission — no `capture`, shows file picker */}
      <input
        ref={heroGalleryInputRef}
        type="file"
        accept="image/jpeg"
        style={s.hiddenInput}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => onNativePhotoSelected("hero", e)}
      />
      <input
        ref={missionGalleryInputRef}
        type="file"
        accept="image/jpeg"
        style={s.hiddenInput}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => onNativePhotoSelected("mission", e)}
      />
      {/* No `capture` attr: lets the OS show the gallery picker with
          multi-select. Photographers shoot a burst in the native camera app
          first, then import them all here in one tap. */}
      <input
        ref={markerImportRef}
        type="file"
        accept="image/jpeg"
        multiple
        style={s.hiddenInput}
        tabIndex={-1}
        aria-hidden="true"
        onChange={onMarkersImported}
      />

      <div style={s.controls}>
        <button
          style={s.captureBtn}
          onClick={useNativeCamera ? triggerNativeCamera : takePhoto}
          disabled={!useNativeCamera && !!cameraError}
          aria-label={captureLabel[activeSection]}
        >
          {captureLabel[activeSection]}
        </button>
        {useNativeCamera && (
          <button
            style={s.importBtn}
            onClick={triggerGalleryPicker}
            aria-label={`Upload ${activeSection} photo from files`}
          >
            From file
          </button>
        )}
        {activeSection === "markers" && (
          <button
            style={s.importBtn}
            onClick={() => markerImportRef.current?.click()}
            aria-label="Import marker photos from gallery"
          >
            Import from gallery
          </button>
        )}
      </div>

      {/* Hero thumbnail */}
      {activeSection === "hero" && heroThumb && (
        <div style={s.singleThumbWrap}>
          <img src={heroThumb} alt="Hero photo" style={s.singleThumb} />
        </div>
      )}

      {/* Mission thumbnail */}
      {activeSection === "mission" && missionThumb && (
        <div style={s.singleThumbWrap}>
          <img src={missionThumb} alt="Mission photo" style={s.singleThumb} />
        </div>
      )}

      {/* Marker thumbnails */}
      {activeSection === "markers" && markerThumbs.length > 0 && (
        <ul style={s.thumbList} aria-label="Marker photos">
          {markerThumbs.map((src, i) => (
            <li key={i} style={s.thumbItem}>
              <button
                style={s.thumbBtn}
                onClick={() => retakeMarker(i)}
                aria-label={`Retake marker ${i + 1}`}
                title="Tap to retake"
              >
                <img src={src} alt={`Marker ${i + 1}`} style={s.thumb} />
                <span style={s.retakeLabel}>Retake</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <button
        style={{
          ...s.proceedBtn,
          ...(!canProceed ? s.proceedBtnDisabled : {}),
        }}
        onClick={() => onCapture({ hero: hero!, mission: mission!, markers })}
        disabled={!canProceed}
        aria-disabled={!canProceed}
      >
        Proceed{canProceed ? "" : ` (need hero + mission + ${MIN_MARKERS} markers)`}
      </button>
    </div>
  );
}

// -- Inline styles ------------------------------------------------------------

const s: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  heading: {
    fontSize: 17,
    fontWeight: 700,
    color: "#111",
  },
  tabRow: {
    display: "flex",
    gap: 4,
  },
  tab: {
    flex: 1,
    padding: "8px 4px",
    borderRadius: 6,
    border: "1px solid #ddd",
    background: "#f5f5f5",
    color: "#444",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    textAlign: "center",
  },
  tabActive: {
    background: "#c8102e",
    color: "#fff",
    borderColor: "#c8102e",
  },
  tabDone: {
    borderColor: "#2d7a2d",
    color: "#2d7a2d",
  },
  sectionLabel: {
    fontSize: 13,
    color: "#666",
    margin: 0,
  },
  viewfinderWrap: {
    position: "relative",
    width: "100%",
    aspectRatio: "4/3",
    background: "#000",
    borderRadius: 8,
    overflow: "hidden",
  },
  video: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
  },
  overlay: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    padding: "8px 12px",
    background: "rgba(0,0,0,0.5)",
    zIndex: 1,
    pointerEvents: "none",
  },
  overlayText: {
    fontSize: 12,
    color: "#fff",
    lineHeight: 1.4,
  },
  controls: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  captureBtn: {
    minHeight: 48,
    padding: "0 24px",
    borderRadius: 8,
    border: "none",
    background: "#c8102e",
    color: "#fff",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    flex: 1,
  },
  importBtn: {
    minHeight: 48,
    padding: "0 16px",
    borderRadius: 8,
    border: "1px solid #c8102e",
    background: "#fff",
    color: "#c8102e",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    flex: 1,
  },
  singleThumbWrap: {
    display: "flex",
    justifyContent: "center",
  },
  singleThumb: {
    width: 120,
    height: 90,
    objectFit: "cover",
    borderRadius: 6,
    border: "2px solid #2d7a2d",
  },
  thumbList: {
    listStyle: "none",
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    padding: 0,
    margin: 0,
  },
  thumbItem: {
    listStyle: "none",
  },
  thumbBtn: {
    background: "none",
    border: "2px solid #e0e0e0",
    borderRadius: 6,
    padding: 0,
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    overflow: "hidden",
    width: 72,
  },
  thumb: {
    width: 72,
    height: 54,
    objectFit: "cover",
    display: "block",
  },
  retakeLabel: {
    fontSize: 10,
    color: "#666",
    padding: "2px 0 3px",
  },
  proceedBtn: {
    minHeight: 48,
    padding: "0 24px",
    borderRadius: 8,
    border: "none",
    background: "#c8102e",
    color: "#fff",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    alignSelf: "stretch",
  },
  proceedBtnDisabled: {
    background: "#e0e0e0",
    color: "#999",
    cursor: "not-allowed",
  },
  error: {
    fontSize: 13,
    color: "#c0392b",
    background: "#fdf0ef",
    padding: "8px 12px",
    borderRadius: 8,
  },
  nativeHint: {
    fontSize: 13,
    color: "#444",
    background: "#f5f5f5",
    padding: "12px",
    borderRadius: 8,
    margin: 0,
    lineHeight: 1.4,
  },
  hiddenInput: {
    position: "absolute",
    width: 1,
    height: 1,
    opacity: 0,
    pointerEvents: "none",
  },
};
